// ChapterMembersTab.jsx — Chapter Members directory, extracted from
// LimitedPartnerPortal. Read-only grid of the signed-in user's chapter
// members as raised chrome panels with role chip, LinkedIn button,
// member-since date, and up to eight most recent badges (rendered from
// BADGES definitions by badgeId).
import React from "react";
import { BADGES } from "../../../data/badgeDefinitions";
import BadgeIcon from "../../icons/BadgeIcon";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];

export default function ChapterMembersTab({ chapterMembers }) {
  const chapterName = chapterMembers.find((m) => m && m.chapter)?.chapter;
  const directorCount = chapterMembers.filter((m) => {
    const displayRole = m.role === 'superAdmin' ? m.chapterRole : m.role;
    return displayRole === 'chapter_director';
  }).length;

  return (
        <div className="lp-tab-page">
          {/* Pane header — chapter context, title, member counts */}
          <div className="win95-pane-header">
            <div>
              <div className="win95-pane-header__context">{chapterName || "Your chapter"}</div>
              <h2 className="win95-pane-header__title">Chapter members</h2>
            </div>
            <div className="win95-pane-header__stats">
              <div className="win95-statpanel">
                <div className="win95-statpanel__label">Members</div>
                <div className="win95-statpanel__figure">{chapterMembers.length}</div>
              </div>
              <div className="win95-statpanel">
                <div className="win95-statpanel__label">Directors</div>
                <div className="win95-statpanel__figure">{directorCount}</div>
              </div>
            </div>
          </div>
          <section className="admin-section admin-section--paper">

          {chapterMembers.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--mb-ink-60)', padding: '40px' }}>
              No active members found in your chapter.
            </p>
          ) : (
            <div className="member-cards-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
              gap: '15px'
            }}>
              {chapterMembers
                .sort((a, b) => {
                  // Sort by role (superAdmin > chapter_director > lp), then by name
                  const roleOrder = { superAdmin: 0, chapter_director: 1, lp: 2 };
                  if (roleOrder[a.role] !== roleOrder[b.role]) {
                    return roleOrder[a.role] - roleOrder[b.role];
                  }
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map(member => {
                  const memberBadges = (member.badges || [])
                    .filter(badge => badge.earnedAt || badge.earnedDate)
                    .sort((a, b) => {
                      const dateA = new Date(a.earnedAt || a.earnedDate || 0);
                      const dateB = new Date(b.earnedAt || b.earnedDate || 0);
                      return dateB - dateA;
                    })
                    .slice(0, 8);

                  // Prefer the uploaded photo from /users/{uid}.photoUrl. Fall
                  // back to the legacy /assets/lps/{slug}.png convention so
                  // members who haven't been re-uploaded yet still render.
                  const photoUrl = member.photoUrl
                    || (member.name
                        ? `/assets/lps/${member.name.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}.png`
                        : null);

                  return (
                    <div key={member.id} style={{
                      background: 'var(--chrome-face)',
                      border: '2px solid',
                      borderColor: 'var(--bevel-raised)',
                      boxShadow: 'var(--shadow-raised)',
                      padding: '14px 16px',
                      cursor: 'default'
                    }}>
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'start', marginBottom: '12px' }}>
                        {photoUrl && (
                          <img
                            src={photoUrl}
                            alt={member.name}
                            className="member-photo"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              border: '2px solid',
                              borderColor: 'var(--bevel-sunken)'
                            }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{
                            margin: '0 0 5px 0',
                            fontFamily: 'var(--font-chrome)',
                            fontSize: 'var(--tc-ui-lg)',
                            fontWeight: 700,
                            color: 'var(--chrome-outline)'
                          }}>
                            {member.name || 'Unknown'}
                          </h4>
                          {(() => {
                            const displayRole = member.role === 'superAdmin' ? member.chapterRole : member.role;
                            const roleTag = {
                              chapter_director: { label: 'Chapter director', bg: 'var(--gnf-blue-200)' },
                              lp: { label: 'Limited partner', bg: 'var(--gnf-green-100)' },
                            }[displayRole];
                            if (!roleTag) return null;
                            return (
                              <span className="win95-chip" style={{
                                background: roleTag.bg,
                                color: 'var(--mb-ink)',
                                marginBottom: '6px'
                              }}>
                                {roleTag.label}
                              </span>
                            );
                          })()}
                          {member.professionalRole && (
                            <div style={{
                              fontFamily: 'var(--font-chrome)',
                              fontSize: 'var(--tc-ui)',
                              color: 'var(--chrome-text-soft)',
                              margin: '4px 0 8px'
                            }}>{member.professionalRole}</div>
                          )}
                          {member.linkedinUrl && (
                            <button
                              type="button"
                              className="win95-btn win95-btn--compact"
                              onClick={() => window.open(member.linkedinUrl, '_blank')}
                            >
                              LinkedIn
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Etched divider — chrome shade over highlight */}
                      <div style={{
                        fontFamily: 'var(--font-chrome)',
                        fontSize: '11px',
                        color: 'var(--chrome-text-soft)',
                        paddingTop: '8px',
                        borderTop: '1px solid var(--chrome-shade)',
                        boxShadow: 'inset 0 1px 0 var(--chrome-highlight)'
                      }}>
                        Member since {
                          member.anniversary
                            ? (() => {
                                const date = new Date(member.anniversary.seconds ? member.anniversary.seconds * 1000 : member.anniversary);
                                return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
                              })()
                            : 'unknown'
                        }
                      </div>

                      {memberBadges.length > 0 && (
                        <div style={{
                          borderTop: '1px solid var(--chrome-shade)',
                          boxShadow: 'inset 0 1px 0 var(--chrome-highlight)',
                          marginTop: '8px',
                          paddingTop: '10px',
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          columnGap: '8px',
                          alignItems: 'start',
                          maxWidth: '100%'
                        }}>
                          <span style={{
                            fontFamily: 'var(--font-chrome)',
                            fontSize: 'var(--tc-label)',
                            fontWeight: 700,
                            letterSpacing: 'var(--track-label)',
                            color: 'var(--chrome-text-soft)',
                            paddingTop: '3px'
                          }}>Recent</span>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '5px',
                            alignItems: 'center',
                            minWidth: 0
                          }}>
                            {memberBadges.map((badge, idx) => {
                              const badgeId = badge.badgeId || badge.id;
                              const badgeData = BADGES[badgeId];
                              if (!badgeData) return null;

                              return (
                                <span key={idx} className="win95-chip" style={{
                                  background: 'var(--gnf-bg)'
                                }} title={badgeData.description}>
                                  <BadgeIcon id={badgeId} size={13} />
                                  {badgeData.name.split(' ').slice(1).join(' ')}
                                </span>
                              );
                            })}
                            {member.badges && member.badges.length > 8 && (
                              <span style={{
                                fontFamily: 'var(--font-chrome)',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: 'var(--chrome-text-soft)'
                              }}>
                                +{member.badges.length - 8} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
          </section>
        </div>
  );
}
